/**
 * Seletor de idioma.
 *
 * A troca de idioma NÃO passa por aqui: cada opção é um <button type="submit">
 * dentro do {% form 'localization' %} nativo, e é o browser que submete. Este
 * ficheiro só trata do painel, do teclado, e de duas coisas que o Liquid não
 * consegue fazer sozinho — ler a query string e gravar um cookie.
 */

import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/** Marca que o visitante já escolheu idioma. Tem de ser igual ao de locale-detector.js. */
const DETECTED_COOKIE = 'aa_locale_detected';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * @typedef {object} PickerRefs
 * @property {HTMLButtonElement} [trigger] - O botão que abre o painel. Só existe no modo dropdown.
 * @property {HTMLDivElement} [panel] - O painel. Só existe no modo dropdown.
 * @property {HTMLButtonElement[]} options - Os botões de idioma.
 *
 * @extends {Component<PickerRefs>}
 */
class LanguageSelectorComponent extends Component {
  get isDropdown() {
    return this.dataset.selectorStyle === 'dropdown';
  }

  get isHidden() {
    return this.refs.panel?.hasAttribute('hidden') ?? true;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#syncReturnTo();
    this.addEventListener('click', this.#onOptionClick);

    if (this.isDropdown) {
      this.addEventListener('keydown', this.#onKeyDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onOptionClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onClickOutside);
  }

  /**
   * O Liquid só conhece request.path, que perde a query string. Sem isto, trocar
   * de idioma numa coleção filtrada deitaria fora os filtros.
   *
   * O campo é gerado pelo {% form 'localization' %} e por isso não tem ref —
   * procura-se pelo nome.
   */
  #syncReturnTo() {
    const path = window.location.pathname + window.location.search;

    for (const input of this.querySelectorAll('input[name="return_to"]')) {
      input.value = path;
    }
  }

  /**
   * Uma escolha manual é definitiva. Sem esta marca, ligar a deteção automática
   * mais tarde voltaria a encaminhar quem já tinha escolhido.
   */
  #onOptionClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('[name="language_code"]')) return;

    // O connectedCallback só sincroniza o return_to uma vez, à montagem. Se a
    // pessoa filtrar ou paginar depois disso, o URL muda por pushState/replaceState
    // (facets.js, paginated-list.js, variant-picker.js) sem o cabeçalho voltar a
    // montar-se, e o return_to fica preso ao URL de quando a página carregou. Sincronizar
    // de novo aqui, mesmo antes de submeter, garante que segue o URL atual.
    this.#syncReturnTo();

    document.cookie = `${DETECTED_COOKIE}=1; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  };

  togglePanel() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.isHidden) return;

    this.refs.panel?.removeAttribute('hidden');
    this.refs.trigger?.setAttribute('aria-expanded', 'true');

    document.addEventListener('click', this.#onClickOutside);
  }

  hidePanel = () => {
    if (this.isHidden) return;

    this.refs.panel?.setAttribute('hidden', '');
    this.refs.trigger?.setAttribute('aria-expanded', 'false');

    document.removeEventListener('click', this.#onClickOutside);
  };

  #onClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.isHidden) return;
      event.preventDefault();
      this.hidePanel();
      this.refs.trigger?.focus();
      return;
    }

    const options = this.refs.options ?? [];
    if (options.length === 0) return;

    const current = options.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        options[current < 0 ? 0 : (current + 1) % options.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        options[current <= 0 ? options.length - 1 : current - 1]?.focus();
        break;
      case 'Home':
        if (current < 0) return;
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        if (current < 0) return;
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
    }
  };
}

if (!customElements.get('language-selector-component')) {
  customElements.define('language-selector-component', LanguageSelectorComponent);
}
